import { FontError } from './errors/errors';
import { getUrlParameters, replaceOrigin } from './url';
// NOTE! Mangling public static functions don't fully resolve correctly in cross reference dependency injections
export class FontLoader {
    static setEnvs(env) {
        FontLoader._creativeConfig = env;
    }
    static setCreativeId(creativeId) {
        FontLoader._creativeId = creativeId;
    }
    static setBrowserDocument(doc) {
        FontLoader._browserDocument = doc;
    }
    /**
     * Register handler for font load failures. Without it, errors are
     * still logged to console but not reported. Each integration (live ad,
     * Studio, ...) is expected to register its own handler.
     */
    static setErrorHandler(handler) {
        FontLoader._errorHandler = handler;
    }
    /**
     * Clear the handler only if it still matches the one passed in.
     */
    static clearErrorHandler(handler) {
        if (FontLoader._errorHandler === handler) {
            FontLoader._errorHandler = undefined;
        }
    }
    static _reportError(err, fontURL, loadContext) {
        const fontError = new FontError(err instanceof Error ? err : String(err), {
            url: fontURL,
            text: loadContext === null || loadContext === void 0 ? void 0 : loadContext.text,
            isFeeded: loadContext === null || loadContext === void 0 ? void 0 : loadContext.isFeeded
        });
        if (FontLoader._errorHandler) {
            FontLoader._errorHandler(fontError);
        }
        else {
            console.error(fontError.message, err);
        }
    }
    /**
     * Loads (and registers) the font face. Failures are swallowed and routed to
     * `_reportError`; the boolean return lets callers react to a failed load
     * (e.g. to log diagnostics) without the error propagating.
     *
     * @returns `true` if the font face loaded, `false` if it failed.
     */
    static async loadFontFace(fontStyle, fontServiceOrigin, loadContext) {
        const inStudioApp = FontLoader._creativeConfig.environment === 1 /* AdEnvironment.App */;
        try {
            const fontFace = await FontLoader.injectFontFace(fontStyle, fontServiceOrigin, inStudioApp);
            if (!FontLoader._browserDocument.fonts.has(fontFace)) {
                FontLoader._browserDocument.fonts.add(fontFace);
            }
            return true;
        }
        catch (err) {
            const fontUrl = getFontURL(fontStyle, fontServiceOrigin);
            FontLoader._reportError(err, fontUrl, loadContext);
            return false;
        }
    }
    static async injectFontStyle(fontStyle, fontServiceOrigin, loadContext) {
        const fontURL = getFontURL(fontStyle, fontServiceOrigin);
        if (!fontURL) {
            FontLoader._reportError('Failed to get URL of font style', fontURL, loadContext);
            return;
        }
        const document = FontLoader._browserDocument;
        const fontFamily = `f-${fontStyle.id}`;
        const fontType = getFontExtension(fontURL);
        const fontPreloadLink = document.createElement('link');
        fontPreloadLink.rel = 'preload';
        fontPreloadLink.href = fontURL;
        fontPreloadLink.as = 'font';
        fontPreloadLink.type = `font/${fontType}`;
        fontPreloadLink.crossOrigin = '';
        const fontStyleSheet = document.createElement('style');
        fontStyleSheet.textContent = `@font-face { font-family: '${fontFamily}'; src: url(${fontURL}) format('${fontType}'); }`;
        document.head.appendChild(fontPreloadLink);
        document.head.appendChild(fontStyleSheet);
        try {
            await document.fonts.load(`1em ${fontFamily}`);
        }
        catch (err) {
            FontLoader._reportError(err, fontURL, loadContext);
        }
    }
    static async injectFontFace(fontStyle, fontServiceOrigin, trimZWNJ = false) {
        const fontURL = getFontURL(fontStyle, fontServiceOrigin);
        let fontFace = FontLoader._fontStyleMap.get(fontStyle.id);
        const preloadedFontURL = FontLoader._fontStyleURLMap.get(fontStyle.id);
        if (fontFace && preloadedFontURL === fontURL) {
            return await fontFace.load();
        }
        if (fontURL) {
            // Skip U+200C (Zero Width Non Joiner), it should always use fallback
            const unicodeRange = trimZWNJ ? 'U+0-200B, U+200E-10FFFF' : undefined;
            fontFace = new FontFace(`f-${fontStyle.id}`, `url(${fontURL})`, {
                unicodeRange
            });
        }
        else {
            throw new Error('Unknown font style.');
        }
        FontLoader._fontStyleMap.set(fontStyle.id, fontFace);
        FontLoader._fontStyleURLMap.set(fontStyle.id, fontURL);
        return await fontFace.load();
    }
}
FontLoader._fontStyleMap = new Map();
FontLoader._fontStyleURLMap = new Map(); // Load font again if the URL are different (i.e.: diff characters in feed)
FontLoader._browserDocument = window.document;
export function getFontExtension(url) {
    var _a;
    return (_a = getUrlParameters(url).u) === null || _a === void 0 ? void 0 : _a.split('.').pop();
}
export function getFontURL(fontStyle, fontServiceOrigin) {
    let url = fontStyle.src || fontStyle.fontUrl;
    const env = FontLoader._creativeConfig;
    if (env.environment !== 3 /* AdEnvironment.Test */) {
        if (env.environment === 1 /* AdEnvironment.App */ && env.origins.FONTS_AZURE_STORAGE_CDN) {
            url = replaceOrigin(url, env.origins.FONTS_AZURE_STORAGE_CDN);
        }
        if (fontServiceOrigin) {
            // notice, url can be base64 and still work
            url = replaceOrigin(url, fontServiceOrigin);
            if (FontLoader._creativeId) {
                const urlObj = new URL(url);
                urlObj.searchParams.append('cid', FontLoader._creativeId);
                url = urlObj.toString();
            }
        }
    }
    return url;
}
export function getFontBlobUrl(url) {
    const fontUrl = new URL(url);
    const encodedFontBlobUrl = fontUrl.searchParams.get('u');
    if (!encodedFontBlobUrl) {
        return url;
    }
    return decodeURIComponent(encodedFontBlobUrl);
}
